/**
 * SquadTab Component
 * 
 * Squad builder tab containing the pitch formation display, number selector,
 * bench section, captain selection, and player selector list.
 */
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { BenchSection } from './BenchSection';
import { PlayerSelector } from './PlayerSelector';
import { MATCH_FORMATS, FORMATION_POSITIONS } from './constants';

/**
 * @param {Object} props
 * @param {Object} props.squadState - All squad-related state
 * @param {Object} props.squadHandlers - All squad-related event handlers
 * @param {Object[]} props.players - All player objects
 * @param {boolean} props.isViewOnly - Whether squad is read-only
 * @param {Object} props.user - Current user (for button_color, preset reasons)
 * @param {Object} props.refs - pitchRef and playerListRef
 */
export function SquadTab({
  squadState,
  squadHandlers,
  players,
  isViewOnly,
  user,
  refs,
  importedMeta,
  onDismissImportedMeta,
}) {
  const {
    matchFormat, benchSlots, formation, lineup, subs,
    assignedNumbers, subNumbers, captainId,
    selectedPosition, selectedBenchPosition,
    draggedPosition, dragOverPosition,
    numberSelectOpen, numberSelectPosition, pendingPlayerSelect,
    availablePlayers, availableFormations, searchQuery,
    nonSelectionReasons
  } = squadState;

  const {
    handleFormatChange, setFormation, setBenchSlots,
    handlePositionClick, handlePlayerDoubleClick,
    handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd,
    handleNumberSelect, getAvailableNumbers,
    handleBenchPositionClick, handleBenchDoubleClick, handleRemoveSub,
    setCaptainId, handlePlayerSelect, handleAddSub,
    setSearchQuery, setNonSelectionReasons,
    setNumberSelectOpen, setNumberSelectPosition, setPendingPlayerSelect, setSelectedBenchPosition
  } = squadHandlers;

  const { pitchRef, playerListRef } = refs;

  const positions = FORMATION_POSITIONS[formation] || FORMATION_POSITIONS['4-4-2'];
  const squadCount = lineup.filter(Boolean).length + subs.filter(s => s && s.playerId).length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="squad-tab-content">
      {/* Phase 1.75: Screenshot Import summary banner — surfaces detected
          names that couldn't be fuzzy-matched to the roster so the coach
          knows exactly what to complete. */}
      {importedMeta && !isViewOnly && (
        <div
          className="lg:col-span-2 rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-2"
          data-testid="squad-imported-meta-banner"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-black text-amber-900 uppercase tracking-widest">Screenshot import applied</p>
              <p className="text-xs text-amber-800 mt-0.5">
                {importedMeta.total_starters} starter{importedMeta.total_starters === 1 ? '' : 's'} &middot; {importedMeta.total_subs} sub{importedMeta.total_subs === 1 ? '' : 's'} &middot; {importedMeta.total_events} event{importedMeta.total_events === 1 ? '' : 's'} imported.
                {(importedMeta.unmatched_starters.length + importedMeta.unmatched_subs.length) > 0 && (
                  <>
                    {' '}
                    <strong>{importedMeta.unmatched_starters.length + importedMeta.unmatched_subs.length} need review</strong> &mdash; detected on the screenshot but couldn&apos;t be matched to your roster.
                  </>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={onDismissImportedMeta}
              className="text-xs font-bold uppercase tracking-widest text-amber-700 hover:text-amber-900 shrink-0"
              data-testid="squad-imported-meta-dismiss"
            >
              Dismiss
            </button>
          </div>

          {(importedMeta.unmatched_starters.length > 0 || importedMeta.unmatched_subs.length > 0) && (
            <div className="pt-2 border-t border-amber-200 space-y-1">
              {importedMeta.unmatched_starters.map((p, i) => (
                <div key={`us-${i}`} className="flex items-center justify-between text-xs gap-2">
                  <span className="text-amber-900 font-semibold truncate">
                    <span className="inline-block w-6 h-6 rounded-full bg-amber-200 text-center mr-2 text-[10px] leading-6">S</span>
                    {p.number ? `#${p.number} ` : ''}{p.name}
                  </span>
                  {p.candidates?.length > 0 && (
                    <span className="flex items-center gap-1 shrink-0">
                      {p.candidates.slice(0, 2).map((c) => (
                        <button
                          key={c.player_id}
                          type="button"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/matches/players/aliases`, {
                                method: 'POST',
                                headers: {
                                  Authorization: `Bearer ${token}`,
                                  'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({ alias_name: p.name, player_id: c.player_id, source: 'screenshot_import' }),
                              });
                              onDismissImportedMeta?.();
                            } catch { /* fail silently — coach can still assign manually */ }
                          }}
                          className="text-[10px] font-bold uppercase tracking-wider bg-amber-200 hover:bg-amber-300 text-amber-900 px-1.5 py-0.5 rounded transition-colors"
                          title={`Save "${p.name}" → ${c.name} for next time`}
                          data-testid={`alias-suggest-${i}-${c.player_id}`}
                        >
                          {c.name}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
              ))}
              {importedMeta.unmatched_subs.map((p, i) => (
                <div key={`ub-${i}`} className="flex items-center justify-between text-xs gap-2">
                  <span className="text-amber-900 font-semibold truncate">
                    <span className="inline-block w-6 h-6 rounded-full bg-amber-200 text-center mr-2 text-[10px] leading-6">B</span>
                    {p.number ? `#${p.number} ` : ''}{p.name}
                  </span>
                  {p.candidates?.length > 0 && (
                    <span className="flex items-center gap-1 shrink-0">
                      {p.candidates.slice(0, 2).map((c) => (
                        <button
                          key={c.player_id}
                          type="button"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/matches/players/aliases`, {
                                method: 'POST',
                                headers: {
                                  Authorization: `Bearer ${token}`,
                                  'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({ alias_name: p.name, player_id: c.player_id, source: 'screenshot_import' }),
                              });
                              onDismissImportedMeta?.();
                            } catch { /* fail silently */ }
                          }}
                          className="text-[10px] font-bold uppercase tracking-wider bg-amber-200 hover:bg-amber-300 text-amber-900 px-1.5 py-0.5 rounded transition-colors"
                          title={`Save "${p.name}" → ${c.name} for next time`}
                          data-testid={`alias-suggest-sub-${i}-${c.player_id}`}
                        >
                          {c.name}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
              ))}
              <p className="text-[11px] text-amber-700 pt-1 italic">
                Tap a candidate to teach the app this name for next time, then assign the player on the pitch below.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Left: Pitch + Bench */}
      <div className="space-y-4">
        <Card className="rounded-2xl" ref={pitchRef}>
          <CardContent className="p-4">
            {/* Match Format & Bench Slots Settings */}
            {!isViewOnly && (
              <div className="flex items-center gap-4 mb-4 pb-4 border-b">
                <div className="flex-1">
                  <Label className="text-xs text-gray-500 mb-1 block">Match Format</Label>
                  <Select value={matchFormat} onValueChange={handleFormatChange}>
                    <SelectTrigger className="w-full rounded-xl" data-testid="match-format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MATCH_FORMATS.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-24">
                  <Label className="text-xs text-gray-500 mb-1 block">Bench Slots</Label>
                  <Input
                    type="number"
                    min="0"
                    max="12"
                    value={benchSlots}
                    onChange={(e) => setBenchSlots(parseInt(e.target.value) || 0)}
                    className="rounded-xl"
                    data-testid="bench-slots-input"
                  />
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Formation</h3>
              {isViewOnly ? (
                <span className="text-gray-600 font-medium">{formation}</span>
              ) : (
                <Select value={formation} onValueChange={setFormation}>
                  <SelectTrigger className="w-32 rounded-xl" data-testid="formation-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFormations.map(f => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Pitch Display */}
            <div 
              className="relative rounded-xl overflow-hidden"
              style={{ 
                aspectRatio: '2/3',
                backgroundImage: 'url(https://customer-assets.emergentagent.com/job_9fdf8e2b-a210-4b58-8435-eeb33315a6e2/artifacts/kkyzv9x6_Untitled%20design%20%2872%29.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
              data-testid="pitch-display"
            >
              {/* Position markers */}
              {positions.map((pos, index) => {
                const playerId = lineup[index];
                const player = playerId ? players.find(p => p.id === playerId) : null;
                const isSelected = selectedPosition === index;
                const assignedNumber = assignedNumbers[index];
                const isCaptain = playerId && playerId === captainId;

                return (
                  <div
                    key={pos.id}
                    className={`absolute transform -translate-x-1/2 -translate-y-1/2 ${isViewOnly ? '' : 'cursor-pointer'} transition-all ${
                      isSelected ? 'scale-110 z-10' : ''
                    } ${draggedPosition === index ? 'opacity-50 scale-95' : ''} ${
                      dragOverPosition === index ? 'scale-110' : ''
                    }`}
                    style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                    draggable={!isViewOnly && !!player}
                    onDragStart={(e) => !isViewOnly && handleDragStart(e, index)}
                    onDragOver={(e) => !isViewOnly && handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => !isViewOnly && handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    onClick={() => !isViewOnly && !player && handlePositionClick(index)}
                    onDoubleClick={() => !isViewOnly && player && handlePlayerDoubleClick(index)}
                  >
                    <div className="relative">
                      <div 
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold shadow-lg transition-all ${
                          player 
                            ? dragOverPosition === index 
                              ? 'bg-amber-400 text-amber-900 ring-2 ring-amber-300'
                              : 'bg-white text-emerald-800' 
                            : isSelected 
                            ? 'bg-amber-400 text-amber-900 ring-2 ring-amber-300' 
                            : dragOverPosition === index
                            ? 'bg-amber-400/50 text-amber-900 ring-2 ring-amber-300'
                            : 'bg-white/30 text-white border-2 border-dashed border-white/50'
                        }`}
                        onClick={(e) => {
                          if (!isViewOnly && player) {
                            e.stopPropagation();
                            handlePositionClick(index);
                          }
                        }}
                      >
                        {player ? (assignedNumber || '') : pos.label}
                      </div>
                      {/* Captain Badge */}
                      {isCaptain && (
                        <div 
                          className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-amber-500 text-white rounded-full flex items-center justify-center text-[8px] sm:text-[10px] font-bold shadow-md border border-white"
                          data-testid="captain-badge"
                        >
                          C
                        </div>
                      )}
                    </div>
                    {player && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap">
                        <span className="text-xs font-medium text-white bg-black/50 px-1.5 py-0.5 rounded">
                          {player.name?.split(' ').pop()}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Number Selection Popup */}
              {numberSelectOpen && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20 rounded-xl">
                  <div className="bg-white rounded-xl p-4 shadow-xl max-w-[300px] w-full mx-4">
                    {(pendingPlayerSelect || (numberSelectPosition !== null && lineup[numberSelectPosition])) && (
                      <div className="text-center mb-3 pb-3 border-b">
                        <p className="text-sm text-gray-500">
                          {pendingPlayerSelect ? 'Assigning number for' : 'Change number for'}
                        </p>
                        <p className="font-semibold text-gray-900">
                          {pendingPlayerSelect 
                            ? players.find(p => p.id === pendingPlayerSelect.playerId)?.name 
                            : players.find(p => p.id === lineup[numberSelectPosition])?.name || 'Player'}
                        </p>
                      </div>
                    )}
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 text-center">Select Shirt Number</h4>
                    
                    {/* Use Squad Number Option */}
                    {(() => {
                      const pId = pendingPlayerSelect?.playerId || (numberSelectPosition !== null ? lineup[numberSelectPosition] : null);
                      const player = pId ? players.find(p => p.id === pId) : null;
                      const squadNumber = player?.jersey_number;
                      const isAvailable = squadNumber && !Object.values(assignedNumbers).includes(squadNumber);
                      
                      if (squadNumber && isAvailable) {
                        return (
                          <button
                            onClick={() => handleNumberSelect(squadNumber)}
                            className="w-full mb-3 py-2.5 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                          >
                            Use Squad Number: #{squadNumber}
                          </button>
                        );
                      }
                      return null;
                    })()}
                    
                    <p className="text-xs text-gray-500 mb-2 text-center">Or select a number:</p>
                    <div className="grid grid-cols-5 gap-2 max-h-[200px] overflow-y-auto">
                      {getAvailableNumbers().map(num => {
                        const pId = pendingPlayerSelect?.playerId || (numberSelectPosition !== null ? lineup[numberSelectPosition] : null);
                        const player = pId ? players.find(p => p.id === pId) : null;
                        const isSquadNumber = player?.jersey_number === num;
                        
                        return (
                          <button
                            key={num}
                            onClick={() => handleNumberSelect(num)}
                            className={`w-10 h-10 rounded-full font-bold text-sm flex items-center justify-center transition-colors ${
                              isSquadNumber
                                ? 'bg-emerald-600 text-white'
                                : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800'
                            }`}
                          >
                            {num}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => {
                        setNumberSelectOpen(false);
                        setNumberSelectPosition(null);
                        setPendingPlayerSelect(null);
                        setSelectedBenchPosition(null);
                      }}
                      className="mt-3 w-full py-2 text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bench Section */}
        <BenchSection
          subs={subs}
          players={players}
          onBenchPositionClick={handleBenchPositionClick}
          onBenchDoubleClick={handleBenchDoubleClick}
          onRemoveSub={handleRemoveSub}
          isViewOnly={isViewOnly}
          selectedBenchPosition={selectedBenchPosition}
          subNumbers={subNumbers}
          benchSlots={benchSlots}
        />

        {/* Captain Selection */}
        {!isViewOnly && lineup.filter(Boolean).length > 0 && (
          <Card className="rounded-2xl" data-testid="captain-selection-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <span className="w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold">C</span>
                  Select Captain
                </h3>
                {captainId && (
                  <button
                    onClick={() => setCaptainId(null)}
                    className="text-xs text-gray-500 hover:text-red-500 transition-colors"
                    data-testid="clear-captain-btn"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {lineup.filter(Boolean).map((playerId) => {
                  const player = players.find(p => p.id === playerId);
                  const posIndex = lineup.indexOf(playerId);
                  const shirtNumber = assignedNumbers[posIndex];
                  const isCaptain = playerId === captainId;
                  
                  return (
                    <button
                      key={playerId}
                      onClick={() => setCaptainId(isCaptain ? null : playerId)}
                      className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
                        isCaptain 
                          ? 'bg-amber-50 border-amber-500 ring-1 ring-amber-500' 
                          : 'bg-white border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                      }`}
                      data-testid={`captain-option-${playerId}`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        isCaptain ? 'bg-amber-500 text-white' : 'bg-emerald-100 text-emerald-800'
                      }`}>
                        {shirtNumber || '?'}
                      </div>
                      <span className="text-sm font-medium text-gray-900 truncate flex-1 text-left">
                        {player?.name?.split(' ').pop() || 'Unknown'}
                      </span>
                      {isCaptain && (
                        <span className="w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold">
                          C
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {captainId && (
                <p className="mt-3 text-xs text-gray-500 text-center">
                  Captain: <span className="font-medium text-gray-700">{players.find(p => p.id === captainId)?.name || 'Unknown'}</span>
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Right: Player Selection */}
      <PlayerSelector
        ref={playerListRef}
        availablePlayers={availablePlayers}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onPlayerClick={(playerId) => (selectedPosition !== null || selectedBenchPosition !== null) ? handlePlayerSelect(playerId) : handleAddSub(playerId)}
        isViewOnly={isViewOnly}
        selectedPosition={selectedPosition}
        selectedBenchPosition={selectedBenchPosition}
        nonSelectionReasons={nonSelectionReasons}
        onReasonChange={(playerId, reason) => {
          setNonSelectionReasons(prev => {
            const updated = { ...prev };
            if (reason) {
              updated[playerId] = reason;
            } else {
              delete updated[playerId];
            }
            return updated;
          });
        }}
        presetReasons={user?.preset_non_selection_reasons || ['Injured', 'Suspended', 'Dropped', 'Holiday', 'Work', 'Unavailable']}
      />
    </div>
  );
}
